/**
 * hooks/useRiskConfiguration.ts
 * State management hook for risk configuration
 *
 * Manages local profile editing state and syncs with onboarding draft
 */

// 1. External dependencies
import { useState, useCallback, useEffect, useMemo } from 'react';

// 2. Internal absolute imports
import { generateUUID } from '@/utils/uuid';
import type { OnboardingDraft } from '@/types/transient/OnboardingDraft';

// 3. Relative imports
import { useOnboardingData } from '../../../hooks/useOnboardingData';
import { validateProfileName } from '../utils/riskValidation';

/**
 * Risk profile type from draft
 */
type RiskProfile = NonNullable<OnboardingDraft['trading']['riskConfiguration']>['profiles'][number];
type RiskSpec = RiskProfile['accountDefault'];

/**
 * Hook return type
 */
export interface UseRiskConfigurationReturn {
  // State
  profiles: RiskProfile[];
  selectedProfileId: string | null;
  selectedProfile: RiskProfile | null;
  defaultProfileId: string | null;

  // Profile CRUD
  createProfile: (name: string, description?: string, color?: string) => string;
  updateProfile: (profileId: string, updates: Partial<RiskProfile>) => void;
  deleteProfile: (profileId: string) => boolean;
  setSelectedProfile: (profileId: string | null) => void;
  setDefaultProfile: (profileId: string | null) => void;

  // Account default risk
  updateAccountDefault: (profileId: string, risk: RiskSpec) => void;

  // Strategy overrides
  addStrategyOverride: (profileId: string, strategyTempId: string, risk: RiskSpec) => void;
  updateStrategyOverride: (profileId: string, strategyTempId: string, risk: RiskSpec) => void;
  removeStrategyOverride: (profileId: string, strategyTempId: string) => void;

  // Playbook overrides
  addPlaybookOverride: (profileId: string, playbookTempId: string, risk: RiskSpec) => void;
  updatePlaybookOverride: (profileId: string, playbookTempId: string, risk: RiskSpec) => void;
  removePlaybookOverride: (profileId: string, playbookTempId: string) => void;

  // Validation
  canDeleteProfile: (profileId: string) => boolean;
  isProfileNameUnique: (name: string, excludeProfileId?: string) => boolean;

  // Utilities
  getProfileById: (profileId: string) => RiskProfile | undefined;
}

/**
 * Risk configuration state management hook
 */
export const useRiskConfiguration = (): UseRiskConfigurationReturn => {
  const { draft, updateRiskConfiguration } = useOnboardingData();

  // Local state for selected profile
  const [selectedProfileId, setSelectedProfileId] = useState<string | null>(null);

  // Get risk configuration from draft
  const riskConfig = draft?.trading?.riskConfiguration;
  const profiles = useMemo(() => riskConfig?.profiles || [], [riskConfig?.profiles]);
  const defaultProfileId = riskConfig?.defaultProfileTempId || null;

  // Initialize selected profile if none selected
  useEffect(() => {
    if (!selectedProfileId && profiles.length > 0) {
      setSelectedProfileId(profiles[0]?.tempId || null);
    }
  }, [selectedProfileId, profiles]);

  // Get currently selected profile
  const selectedProfile = useMemo(
    () => profiles.find(p => p.tempId === selectedProfileId) || null,
    [profiles, selectedProfileId]
  );

  // Sync local changes to draft
  const syncToStorage = useCallback((updatedProfiles: RiskProfile[], updatedDefaultId?: string | null) => {
    const config: { profiles: RiskProfile[]; defaultProfileTempId?: string } = {
      profiles: updatedProfiles
    };

    // Handle default profile ID
    if (updatedDefaultId !== undefined) {
      // Explicitly setting or clearing the default
      if (updatedDefaultId !== null) {
        config.defaultProfileTempId = updatedDefaultId;
      }
      // If updatedDefaultId is null, we intentionally omit it to clear
    } else {
      // Preserve existing default if not explicitly changing it
      if (defaultProfileId !== null) {
        config.defaultProfileTempId = defaultProfileId;
      }
    }

    updateRiskConfiguration(config);
  }, [updateRiskConfiguration, defaultProfileId]);

  // Profile CRUD operations
  const createProfile = useCallback((name: string, description?: string, color?: string): string => {
    const tempId = generateUUID();
    const newProfile: RiskProfile = {
      tempId,
      name,
      ...(description !== undefined && { description }),
      ...(color !== undefined && { color }),
      order: profiles.length,
      accountDefault: {
        type: 'percentage',
        value: 0.01 // Default to 1%
      }
    };

    const updatedProfiles = [...profiles, newProfile];
    syncToStorage(updatedProfiles);
    setSelectedProfileId(tempId);

    return tempId;
  }, [profiles, syncToStorage]);

  const updateProfile = useCallback((profileId: string, updates: Partial<RiskProfile>) => {
    const updatedProfiles = profiles.map(profile => {
      if (profile.tempId === profileId) {
        const updated = { ...profile, ...updates };
        // Remove description if it's empty string
        if ('description' in updates && updates.description === '') {
          const { description: _description, ...rest } = updated;
          return rest as RiskProfile;
        }
        return updated;
      }
      return profile;
    });
    syncToStorage(updatedProfiles);
  }, [profiles, syncToStorage]);

  const deleteProfile = useCallback((profileId: string): boolean => {
    // Cannot delete if it's the only profile
    if (profiles.length === 1) {
      return false;
    }

    // Cannot delete default profile without setting new default
    if (profileId === defaultProfileId) {
      return false;
    }

    const updatedProfiles = profiles.filter(p => p.tempId !== profileId);
    syncToStorage(updatedProfiles);

    // Update selected profile if deleted
    if (selectedProfileId === profileId) {
      setSelectedProfileId(updatedProfiles[0]?.tempId || null);
    }

    return true;
  }, [profiles, defaultProfileId, selectedProfileId, syncToStorage]);

  const setDefaultProfile = useCallback((profileId: string | null) => {
    syncToStorage(profiles, profileId);
  }, [profiles, syncToStorage]);

  // Account default risk
  const updateAccountDefault = useCallback((profileId: string, risk: RiskSpec) => {
    const updatedProfiles = profiles.map(profile =>
      profile.tempId === profileId
        ? { ...profile, accountDefault: risk }
        : profile
    );
    syncToStorage(updatedProfiles);
  }, [profiles, syncToStorage]);

  // Strategy overrides
  const addStrategyOverride = useCallback((profileId: string, strategyTempId: string, risk: RiskSpec) => {
    const updatedProfiles = profiles.map(profile => {
      if (profile.tempId !== profileId) return profile;

      const strategyOverrides = profile.strategyOverrides || [];
      return {
        ...profile,
        strategyOverrides: [...strategyOverrides, { strategyTempId, risk }]
      };
    });
    syncToStorage(updatedProfiles);
  }, [profiles, syncToStorage]);

  const updateStrategyOverride = useCallback((profileId: string, strategyTempId: string, risk: RiskSpec) => {
    const updatedProfiles = profiles.map(profile => {
      if (profile.tempId !== profileId) return profile;

      const strategyOverrides = (profile.strategyOverrides || []).map(override =>
        override.strategyTempId === strategyTempId
          ? { ...override, risk }
          : override
      );
      return { ...profile, strategyOverrides };
    });
    syncToStorage(updatedProfiles);
  }, [profiles, syncToStorage]);

  const removeStrategyOverride = useCallback((profileId: string, strategyTempId: string) => {
    const updatedProfiles = profiles.map(profile => {
      if (profile.tempId !== profileId) return profile;

      const strategyOverrides = (profile.strategyOverrides || []).filter(
        override => override.strategyTempId !== strategyTempId
      );
      return { ...profile, strategyOverrides };
    });
    syncToStorage(updatedProfiles);
  }, [profiles, syncToStorage]);

  // Playbook overrides
  const addPlaybookOverride = useCallback((profileId: string, playbookTempId: string, risk: RiskSpec) => {
    const updatedProfiles = profiles.map(profile => {
      if (profile.tempId !== profileId) return profile;

      const playbookOverrides = profile.playbookOverrides || [];
      return {
        ...profile,
        playbookOverrides: [...playbookOverrides, { playbookTempId, risk }]
      };
    });
    syncToStorage(updatedProfiles);
  }, [profiles, syncToStorage]);

  const updatePlaybookOverride = useCallback((profileId: string, playbookTempId: string, risk: RiskSpec) => {
    const updatedProfiles = profiles.map(profile => {
      if (profile.tempId !== profileId) return profile;

      const playbookOverrides = (profile.playbookOverrides || []).map(override =>
        override.playbookTempId === playbookTempId
          ? { ...override, risk }
          : override
      );
      return { ...profile, playbookOverrides };
    });
    syncToStorage(updatedProfiles);
  }, [profiles, syncToStorage]);

  const removePlaybookOverride = useCallback((profileId: string, playbookTempId: string) => {
    const updatedProfiles = profiles.map(profile => {
      if (profile.tempId !== profileId) return profile;

      const playbookOverrides = (profile.playbookOverrides || []).filter(
        override => override.playbookTempId !== playbookTempId
      );
      return { ...profile, playbookOverrides };
    });
    syncToStorage(updatedProfiles);
  }, [profiles, syncToStorage]);

  // Validation helpers
  const canDeleteProfile = useCallback((profileId: string): boolean => {
    // Cannot delete if it's the only profile
    if (profiles.length === 1) return false;

    // Cannot delete default profile
    if (profileId === defaultProfileId) return false;

    return true;
  }, [profiles.length, defaultProfileId]);

  const isProfileNameUnique = useCallback((name: string, excludeProfileId?: string): boolean => {
    return validateProfileName(
      name,
      profiles.map(p => ({ tempId: p.tempId, name: p.name })),
      excludeProfileId
    );
  }, [profiles]);

  const getProfileById = useCallback((profileId: string): RiskProfile | undefined => {
    return profiles.find(p => p.tempId === profileId);
  }, [profiles]);

  return {
    // State
    profiles,
    selectedProfileId,
    selectedProfile,
    defaultProfileId,

    // Profile CRUD
    createProfile,
    updateProfile,
    deleteProfile,
    setSelectedProfile: setSelectedProfileId,
    setDefaultProfile,

    // Account default risk
    updateAccountDefault,

    // Strategy overrides
    addStrategyOverride,
    updateStrategyOverride,
    removeStrategyOverride,

    // Playbook overrides
    addPlaybookOverride,
    updatePlaybookOverride,
    removePlaybookOverride,

    // Validation
    canDeleteProfile,
    isProfileNameUnique,

    // Utilities
    getProfileById
  };
};
